/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import {
  FiHome,
  FiShare2,
  FiFolder,
  FiZap,
  FiList,
  FiInfo,
  FiChevronsLeft,
  FiChevronsRight,
  FiPlus,
} from "react-icons/fi";
import { SideNavItem, SideNavItemConfig } from "./SideNavItem";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { Routes } from "../Utils/Routes";

export interface SideNavProps {
  onAboutClick: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onNewMapping?: () => void;
}

const RAIL_WIDTH_EXPANDED = 210;
const RAIL_WIDTH_COLLAPSED = 60;

export function SideNav({
  onAboutClick,
  collapsed,
  onToggleCollapsed,
  onNewMapping,
}: SideNavProps): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const client = useApolloClient();
  const { currentTheme } = useDexterTheme();

  const mainItems: SideNavItemConfig[] = React.useMemo(
    () => [
      { key: "home", label: "Home", icon: FiHome, url: Routes.HOME },
      {
        key: "mappings",
        label: "Mappings",
        icon: FiShare2,
        url: Routes.MAPPINGS,
      },
      { key: "files", label: "Files", icon: FiFolder, url: Routes.FILES },
      {
        key: "quicktransform",
        label: "Quick Transform",
        icon: FiZap,
        url: Routes.QUICKFORM,
      },
      { key: "jobs", label: "Jobs", icon: FiList, url: Routes.JOBS },
    ],
    []
  );

  const aboutNavItem: SideNavItemConfig = {
    key: "about",
    label: "About",
    icon: FiInfo,
    onClick: onAboutClick,
  };

  // Single-selection active state tracked by KEY, not route
  const computeActiveKeyFromPath = React.useCallback(
    (pathname: string): string => {
      const pathFirstSegment = pathname.split("/")[1] || "";
      const matchingNavItem = mainItems.find(
        (navItem) => (navItem.url ?? "").split("/")[1] === pathFirstSegment
      );
      return matchingNavItem?.key ?? "mappings";
    },
    // mainItems is a stable memoized array; settingsItem is a stable literal
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [activeKey, setActiveKey] = React.useState<string>(() =>
    computeActiveKeyFromPath(location.pathname)
  );

  // Keep the highlight in sync with the URL for navigation that doesn't go
  // through handleSelect (logo link, browser back/forward, deep links).
  React.useEffect(() => {
    setActiveKey(computeActiveKeyFromPath(location.pathname));
  }, [location.pathname, computeActiveKeyFromPath]);

  const handleSelect = React.useCallback(
    (item: SideNavItemConfig) => {
      if (item.onClick) {
        item.onClick();
        return;
      }
      if (item.url) {
        setActiveKey(item.key);
        sessionStorage.clear();
        client.cache.reset();
        history.push(item.url);
      }
    },
    [client.cache, history]
  );

  const railWidth = collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH_EXPANDED;

  return (
    <nav
      aria-label="SideNav"
      style={{
        width: railWidth,
        minWidth: railWidth,
        height: "100%",
        backgroundColor: currentTheme.colors.headerColor,
        color: currentTheme.colors.headerTextColor,
        borderRight: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.15s ease",
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      {/* Mapping Button */}
      <div
        style={{
          padding: collapsed ? "12px 0" : "12px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <button
          onClick={() => {
            if (onNewMapping) {
              onNewMapping();
              return;
            }
            // Default: clear the engine and route to it.
            setActiveKey("mappings");
            sessionStorage.clear();
            client.cache.reset();
            history.push("/");
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            background: currentTheme.colors.primaryButtonColor,
            color: currentTheme.colors.primaryElementFontColor,
            border: "none",
            cursor: "pointer",
            fontFamily: currentTheme.fonts.primary,
            fontWeight: 500,
            fontSize: "0.95rem",
            width: collapsed ? "38px" : "100%",
            height: "38px",
            borderRadius: collapsed ? "10px" : "20px",
            padding: 0,
          }}
        >
          <FiPlus size={18} />
          {!collapsed && <span>New Mapping</span>}
        </button>
      </div>

      {/* Main items */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          paddingTop: "4px",
        }}
      >
        {mainItems.map((item) => (
          <SideNavItem
            key={item.key}
            item={item}
            active={activeKey === item.key}
            collapsed={collapsed}
            headerTextColor={currentTheme.colors.headerTextColor}
            primaryButtonColor={currentTheme.colors.primaryButtonColor}
            selectionBackgroundPrimary={
              currentTheme.colors.selectionBackgroundPrimary
            }
            fontFamily={currentTheme.fonts.primary}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {/* Spacer pushes the bottom row down */}
      <div style={{ flex: 1 }} />

      {/* Bottom row: Settings (left) and collapse control (right) */}
      <div
        style={{
          borderTop: `1px solid ${currentTheme.colors.controlBarBorderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          flexDirection: collapsed ? "column" : "row",
          gap: collapsed ? "2px" : 0,
          paddingBottom: "6px",
          paddingTop: "6px",
        }}
      >
        {/* About: "flex:1" when expanded so it fills the left side */}
        <div style={{ flex: collapsed ? "none" : 1, minWidth: 0 }}>
          <SideNavItem
            item={aboutNavItem}
            active={activeKey === aboutNavItem.key}
            collapsed={collapsed}
            headerTextColor={currentTheme.colors.headerTextColor}
            primaryButtonColor={currentTheme.colors.primaryButtonColor}
            selectionBackgroundPrimary={
              currentTheme.colors.selectionBackgroundPrimary
            }
            fontFamily={currentTheme.fonts.primary}
            onSelect={handleSelect}
          />
        </div>

        {/* Collapse / expand located to the right of settings */}
        <button
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          onClick={onToggleCollapsed}
          style={{
            background: "transparent",
            border: "none",
            color: currentTheme.colors.headerTextColor,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: collapsed ? "8px 0" : "8px 14px",
            flexShrink: 0,
          }}
        >
          {collapsed ? (
            <FiChevronsRight size={18} />
          ) : (
            <FiChevronsLeft size={18} />
          )}
        </button>
      </div>
    </nav>
  );
}
